import React, { useEffect, useState } from 'react';
import api from '../api';

const EMPTY_MOVEMENT = { quantity: '', movement_type: 'in', notes: '' };

function MovementModal({ inventoryId, itemName, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_MOVEMENT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const save = async () => {
    if (!form.quantity || Number(form.quantity) <= 0) return setError('Enter a valid quantity');
    setSaving(true);
    try {
      await api.post(`/inventory/${inventoryId}/movements`, {
        quantity: Number(form.quantity),
        movement_type: form.movement_type,
        notes: form.notes
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Stock Movement</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 16 }}>{itemName}</div>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label>Type</label>
            <select value={form.movement_type} onChange={e => set('movement_type', e.target.value)}>
              <option value="in">Stock In</option>
              <option value="out">Stock Out</option>
              <option value="adjustment">Adjustment</option>
            </select>
          </div>
          <div className="form-group">
            <label>Quantity</label>
            <input type="number" min="1" value={form.quantity} onChange={e => set('quantity', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Record Movement'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InventoryDetail({ item, onClose, onMovement }) {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    api.get(`/inventory/${item.id}/movements`)
      .then(res => { setHistory(res.data.value || []); setLoadingHistory(false); })
      .catch(() => setLoadingHistory(false));
  }, [item.id]);

  const isLow = item.quantity_on_hand <= item.reorder_point;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="modal-title">{item.product_name || item.sku}</span>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.sku}</div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
            <div className="stat-card" style={{ padding: 16 }}>
              <div className="stat-label">On Hand</div>
              <div className="stat-value" style={{ fontSize: 28, color: isLow ? 'var(--danger)' : 'var(--text-primary)' }}>
                {item.quantity_on_hand}
              </div>
              <div className="stat-sub">{item.unit || 'units'}</div>
            </div>
            <div className="stat-card" style={{ padding: 16 }}>
              <div className="stat-label">Reorder Point</div>
              <div className="stat-value" style={{ fontSize: 28 }}>{item.reorder_point || '—'}</div>
              <div className="stat-sub">trigger level</div>
            </div>
            <div className="stat-card" style={{ padding: 16 }}>
              <div className="stat-label">Location</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--text-primary)', marginTop: 8 }}>
                {item.location || '—'}
              </div>
            </div>
          </div>

          {isLow && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              ⚠ Stock below reorder point — consider ordering more
            </div>
          )}

          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase' }}>Movement History</div>
            <button className="btn btn-primary btn-sm" onClick={onMovement}>+ Record Movement</button>
          </div>

          {loadingHistory ? (
            <div className="loading">Loading history...</div>
          ) : history.length === 0 ? (
            <div className="empty-state" style={{ padding: '20px' }}>No movements recorded</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Qty</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>
                        {new Date(h.created_at).toLocaleDateString('en-GB')}
                      </td>
                      <td>
                        <span className={`badge ${h.movement_type === 'in' ? 'badge-active' : h.movement_type === 'out' ? 'badge-inactive' : 'badge-quoted'}`}>
                          {h.movement_type}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: h.movement_type === 'in' ? 'var(--success)' : 'var(--danger)' }}>
                        {h.movement_type === 'in' ? '+' : '-'}{h.quantity}
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{h.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showLowOnly, setShowLowOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showMovement, setShowMovement] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/inventory')
      .then(res => { setInventory(res.data.value || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = inventory.filter(i => {
    const matchSearch = !search ||
      i.product_name?.toLowerCase().includes(search.toLowerCase()) ||
      i.sku?.toLowerCase().includes(search.toLowerCase()) ||
      i.location?.toLowerCase().includes(search.toLowerCase());
    const matchLow = !showLowOnly || i.quantity_on_hand <= i.reorder_point;
    return matchSearch && matchLow;
  });

  const lowStockCount = inventory.filter(i => i.quantity_on_hand <= i.reorder_point).length;

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">INVEN<span>TORY</span></h2>
      </div>

      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-label">Total Items</div>
          <div className="stat-value">{inventory.length}</div>
          <div className="stat-sub">tracked products</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Low Stock</div>
          <div className="stat-value" style={{ color: lowStockCount > 0 ? 'var(--danger)' : 'var(--success)' }}>
            {lowStockCount}
          </div>
          <div className="stat-sub">below reorder point</div>
        </div>
      </div>

      <div className="search-bar">
        <input
          placeholder="Search product, SKU, location..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button
          className={`btn btn-sm ${showLowOnly ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setShowLowOnly(!showLowOnly)}
        >
          ⚠ Low Stock Only
        </button>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>
          {filtered.length} items
        </span>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">Loading inventory...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">No inventory items found</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>On Hand</th>
                  <th>Reorder At</th>
                  <th>Location</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(i => {
                  const isLow = i.quantity_on_hand <= i.reorder_point;
                  return (
                    <tr key={i.id} onClick={() => setSelected(i)}>
                      <td style={{ fontWeight: 600 }}>{i.product_name || '—'}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{i.sku || '—'}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: isLow ? 'var(--danger)' : 'var(--text-primary)' }}>
                        {i.quantity_on_hand} <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 11 }}>{i.unit || ''}</span>
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: 12 }}>{i.reorder_point || '—'}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{i.location || '—'}</td>
                      <td>
                        {isLow
                          ? <span className="badge badge-inactive">⚠ Low Stock</span>
                          : <span className="badge badge-active">OK</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && !showMovement && (
        <InventoryDetail
          item={selected}
          onClose={() => setSelected(null)}
          onMovement={() => setShowMovement(true)}
        />
      )}

      {selected && showMovement && (
        <MovementModal
          inventoryId={selected.id}
          itemName={selected.product_name || selected.sku}
          onClose={() => setShowMovement(false)}
          onSaved={() => { setShowMovement(false); setSelected(null); load(); }}
        />
      )}
    </div>
  );
}